import React, { useState, useEffect } from 'react';
import { useClient } from 'jsonapi-react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { useCheckIncludeProfiles } from '@/hooks/useAuthorityLevel';
import { useAuditorium } from '@/hooks/useAuditorium';
import { Link } from 'react-router-dom';
import { useSession } from '@/hooks/useSession';
import { useNavigate } from 'react-router-dom';
import { useCourse } from '@/hooks/useCourse';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { AuditoriumCard } from './auditorium-card';
import Onboarding from '@/components/custom/onboarding';
import { AuditoriumModalParticipants } from './auditorium-modal-participants';
import usePermission from '@/hooks/usePermission';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import * as ActionCable from '@rails/actioncable';
import moment from 'moment';
import { DashboardContent } from '@/layouts/dashboard';
import { Filter, FilterActions, FilterContent, FilterInput, FilterSelectNative } from '@/components/custom/filter';
import { FiSearch } from 'react-icons/fi';
import { Button } from '@/components/ui/button';
import { CardWrapper } from '@/components/ui/card';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { TiaBanner } from '@/components/custom/tia-banner';
import tiaMonitoria from '@/assets/images/tia/TIA-MONITORIA.png';
import { JOINHA } from '@/pages/dashboard/home-teacher/teacher-character';

const TEN_SECONDS_IN_MILLISECONDS = 10 * 1000;

export function Auditorium(props) {
  const navigate = useNavigate();
  const playfulStudent = usePlayfulStudent();
  const playfulTeacher = usePlayfulTeacher();
  const isPlayful = playfulStudent || playfulTeacher;
  if (!usePermission('Visualizar monitorias', 'Monitorias')) {
    return null;
  }
  const { path, uri } = props;
  const { getAuditoriums, loading, auditoriums } = useAuditorium();
  const { courses, getSimpleCourses } = useCourse();
  const [filteredAuditoriums, setFilteredAuditoriums] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [activeAuditorium, setActiveAuditorium] = useState();
  const [messageChannel, setMessageChannel] = useState(null);
  const { session } = useSession();
  const isAdmin = session.user.profileNames.includes('Administrador');
  const { t } = useTranslation();
  const client = useClient();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const profiles = session?.user?.profileNames || (session?.user && session?.user['profile-names']) || [];

  const apiBaseUrl = client.config.url;
  const cableUrl = apiBaseUrl.replace(/^http(s)?:\/\/(.*?)\/api\/v1\/?$/, 'wss://$2/cable');
  const cable = ActionCable.createConsumer(cableUrl);

  const filters = useFormik({
    initialValues: {
      courses: 'blank',
      gre: 'blank',
      school: 'blank',
      auditorium_name: ''
    }
  });

  if (auditoriums.length === 1 && !isAdmin) {
    navigate(`/auditorio/${auditoriums[0].id}`, { state: { singleAuditorium: true } });
  }

  useEffect(() => {
    if (messageChannel) {
      const auditoriumsStatusChanged = filteredAuditoriums.map(item => {
        if (item.id === Number(messageChannel.auditorium_id)) {
          return {
            ...item,
            status: 'Online',
            updated: moment().format()
          };
        } else {
          return { ...item, updated: null };
        }
      });

      setFilteredAuditoriums(auditoriumsStatusChanged);
    }
  }, [messageChannel]);

  useEffect(() => {
    getSimpleCourses();
    getAuditoriums();
  }, []);

  useEffect(() => {
    if (activeAuditorium) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [activeAuditorium]);

  useEffect(() => {
    const { courses, gre, school, auditorium_name } = filters.values;

    const filtered = auditoriums?.filter(item => {
      let filterResult = true;
      if (courses !== 'blank') {
        filterResult = filterResult && item?.['course-ids']?.includes(parseInt(courses));
      }
      if (gre !== 'blank') {
        filterResult = filterResult && item?.courses?.some(courseItem => courseItem['regional-id'] === parseInt(gre));
      }
      if (school !== 'blank') {
        filterResult = filterResult && item?.courses?.some(courseItem => courseItem['institution-id'] === parseInt(school));
      }

      if (auditorium_name !== '') {
        filterResult = filterResult && item?.name?.toLowerCase().includes(auditorium_name.toLowerCase());
      }

      return filterResult;
    });
    setFilteredAuditoriums(filtered);
  }, [filters.values, auditoriums]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const auditoriumsStatusChanged = filteredAuditoriums.map(item => {
        if (moment(item.updated).isBefore(moment().subtract(30, 'seconds'))) {
          return {
            ...item,
            status: 'Offline'
          };
        } else {
          return item;
        }
      });

      if (auditoriumsStatusChanged.length) {
        setFilteredAuditoriums(auditoriumsStatusChanged);
      }
    }, TEN_SECONDS_IN_MILLISECONDS);

    return () => clearInterval(intervalId);
  }, [filteredAuditoriums]);

  useEffect(() => {
    const subscription = cable.subscriptions.create(
      {
        channel: 'AuditoriumStatusChannel'
      },
      {
        received: data => {
          setMessageChannel(data);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [cableUrl]);

  const breadCrumbs = {
    title: 'Comunidade',
    nav: [
      {
        route: uri,
        name: 'Monitoria',
        isActive: true
      }
    ]
  };

  return (
    <>
      <DashboardContent
        variant="plain"
        breadcrumbs={breadCrumbs}
      >
        {playfulStudent && (
          <TiaBanner
            className="mb-6 md:mt-20"
            image={tiaMonitoria}
            text="Tire suas dúvidas em tempo real com professores e colegas na Monitoria! 💬"
          />
        )}

        {playfulTeacher && (
          <div className="relative mb-6 sm:mt-24">
            <TiaBanner
              className="h-full"
              image={JOINHA}
              imageAlt="Personagem do professor"
              imageHeight={215}
              imageOffset={200}
              bubbleCorner="tl"
              bubbleMaxWidth={600}
              text="Aqui fica a Monitoria! 💬 Acompanhe as turmas em tempo real e tire as dúvidas dos seus alunos por aqui."
            />
          </div>
        )}

        {!useCheckIncludeProfiles({ only: ['student'] }) ? (
          <Filter>
            <FilterContent>
              {/* <FilterSelectNative
                label={'Selecione uma GRE'}
                blankLabel={t('filter.blankLabelAll')}
                value={filters.values.gre}
                onChange={e => {
                  filters.setFieldValue('gre', e.target.value);
                }}
                options={regionals}
              /> */}
              {/* <FilterSelectNative
                label={'Selecione uma escola'}
                blankLabel={t('filter.blankLabelAll')}
                value={filters.values.school}
                onChange={e => {
                  filters.setFieldValue('school', e.target.value);
                }}
                options={istitutions}
              /> */}
              <FilterSelectNative
                label={playfulTeacher ? t('filter.labelSelectCourse') : t('filter.schoolYear')}
                blankLabel={t('filter.blankLabelAll')}
                value={filters.values.courses}
                onChange={e => {
                  filters.setFieldValue('courses', e.target.value);
                }}
                options={courses}
              />
            </FilterContent>

            {usePermission('Criar monitorias', 'Monitorias') && (
              <FilterActions>
                {playfulTeacher ? (
                  <PlayfulCardButton
                    className="h-9 w-auto px-6"
                    onClick={() => navigate('/auditorio/criar/nome')}
                  >
                    {t('auditorium.newAuditorium')}
                  </PlayfulCardButton>
                ) : (
                  <Button
                    wide
                    asChild
                  >
                    <Link to={`/auditorio/criar/nome`}>{t('auditorium.newAuditorium')}</Link>
                  </Button>
                )}
              </FilterActions>
            )}
          </Filter>
        ) : (
          <Filter>
            <FilterContent>
              <FilterInput
                label={t('materials.search')}
                endIcon={FiSearch}
                placeholder="Pesquisar..."
                type="search"
                name="auditoriumSearch"
                value={filters.values.auditorium_name}
                onChange={e => filters.setFieldValue('auditorium_name', e.target.value)}
              />
              <FilterSelectNative
                label={t('filter.labelSelectCourse')}
                blankLabel={t('filter.blankLabelAll')}
                value={filters.values.courses}
                onChange={e => {
                  filters.setFieldValue('courses', e.target.value);
                }}
                options={courses}
              />
            </FilterContent>
          </Filter>
        )}

        {loading && <Loader />}

        {filteredAuditoriums.length === 0 && !loading && <EmptyState type="content" />}

        {!loading &&
          (isPlayful ? (
            <div className="grid animate-fade-in gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredAuditoriums?.map(auditorium => (
                <AuditoriumCard
                  auditorium={auditorium}
                  setActiveAuditorium={setActiveAuditorium}
                  key={auditorium.id}
                />
              ))}
            </div>
          ) : (
            <div className="animate-fade-in">
              <CardWrapper>
                {filteredAuditoriums?.map(auditorium => (
                  <AuditoriumCard
                    auditorium={auditorium}
                    setActiveAuditorium={setActiveAuditorium}
                    key={auditorium.id}
                  />
                ))}
              </CardWrapper>
            </div>
          ))}
      </DashboardContent>

      <Onboarding
        show={showOnboarding}
        setShowOnboarding={setShowOnboarding}
        onClose={() => setShowOnboarding(false)}
        path={path}
        profiles={profiles}
        title={t('onboarding.titleAuditorium')}
      />

      <AuditoriumModalParticipants
        showModal={showModal}
        onClose={() => setShowModal(false)}
        auditorium={activeAuditorium}
      />
    </>
  );
}
